/**
 * Central Feature-Route Mapping
 * Maps routes to their corresponding feature IDs
 * Used by FeatureGuard to protect routes
 */

export const FEATURE_ROUTE_MAP = {
  // Savings & Investments
  '/user/savings': 'savings',
  '/savings': 'savings',
  
  // Investments
  '/user/growth-packages': 'growth_packages',
  '/investments': 'growth_packages',
  
  // Predictions
  '/app/predictions': 'predictions',
  '/user/predictions': 'predictions',
  '/predictions': 'predictions',
  
  // Groups & Community
  '/user/groups': 'groups',
  '/groups': 'groups',
  
  // Marketplace & Commerce
  '/commerce/marketplace': 'marketplace',
  '/commerce/store': 'marketplace',
  '/commerce/product': 'marketplace',
  '/commerce/create-store': 'store_creation',
  '/commerce/dashboard': 'merchant_dashboard',
  '/commerce/cart': 'checkout',
  '/commerce/checkout': 'checkout',
  '/commerce/orders': 'checkout',
  '/user/shop': 'marketplace',
  
  // Matrise Connect (Social)
  '/connect': 'connect_feed',
  '/app/social': 'connect_feed',
  '/connect/feed': 'connect_feed',
  '/user/conversations': 'connect_messaging',
  '/messages': 'connect_messaging',
  '/creator/earnings': 'creator_earnings',
  
  // Wants and Offers Board
  '/wants-offers': 'wants_offers',
  '/wants-offers/browse': 'wants_offers',
  '/wants-offers/my-posts': 'wants_offers',
  '/wants-offers/post': 'wants_offers',
  '/wants-offers/:id': 'wants_offers',
  
  // Matrise Pickup (Rides & Delivery)
  '/pickup': 'pickup_home',
  '/pickup/post': 'pickup_request',
  '/pickup/available': 'pickup_home',
  '/pickup/my-deliveries': 'pickup_home',
  '/pickup/my-jobs': 'pickup_driver',
  '/pickup/driver-registration': 'pickup_driver_registration',
  '/pickup/earnings': 'pickup_driver',
  
  // Matrise Ads
  '/ads': 'ads_platform',
  '/ads/create': 'ads_create_campaign',
  '/ads/analytics': 'ads_analytics',
  
  // Agent Network
  '/agents': 'agent_network',
  '/agents/apply': 'agent_application',
  '/agents/dashboard': 'agent_dashboard',
  
  // Vouchers
  '/user/vouchers': 'vouchers',
  '/vouchers': 'vouchers',
  
  // Referrals
  '/user/referrals': 'referrals',
  '/user/referral': 'referrals',
  '/referrals': 'referrals',
  
  // Tasking & Rewards
  '/user/tasking': 'tasking',
  '/tasks': 'tasking',
  '/earn': 'tasking',
  
  // Support
  '/user/support': 'support',
  '/support': 'support',
  '/help': 'support',
};

/**
 * Get feature ID for a given route path
 * @param {string} pathname - The route pathname
 * @returns {string|null} - Feature ID or null if no mapping
 */
export const getFeatureForRoute = (pathname) => {
  // Direct match
  if (FEATURE_ROUTE_MAP[pathname]) {
    return FEATURE_ROUTE_MAP[pathname];
  }
  
  // Prefix match (for dynamic routes like /commerce/store/:id)
  for (const [route, featureId] of Object.entries(FEATURE_ROUTE_MAP)) {
    if (pathname.startsWith(route)) {
      return featureId;
    }
  }
  
  return null;
};

/**
 * Shortcut definitions with their feature requirements
 */
export const HOMEPAGE_SHORTCUTS = [
  { 
    label: 'Savings', 
    path: '/user/savings', 
    description: 'Manage your plans',
    feature: 'savings'
  },
  { 
    label: 'Referrals', 
    path: '/user/referrals', 
    description: 'Earn rewards',
    feature: 'referrals'
  },
  { 
    label: 'Groups', 
    path: '/user/groups', 
    description: 'Community savings',
    feature: 'groups'
  },
  { 
    label: 'Shop', 
    path: '/commerce/marketplace', 
    description: 'Browse products',
    feature: 'marketplace'
  },
  { 
    label: 'Wants & Offers', 
    path: '/wants-offers', 
    description: 'Post wants & offers',
    feature: 'wants_offers'
  },
  { 
    label: 'Pickup', 
    path: '/pickup', 
    description: 'Request delivery',
    feature: 'pickup_home'
  },
  { 
    label: 'Predictions', 
    path: '/app/predictions', 
    description: 'Join markets',
    feature: 'predictions'
  },
];

/**
 * Discover page categories with their feature requirements
 */
export const DISCOVER_CATEGORIES = [
  {
    title: 'Commerce',
    items: [
      { label: 'MTR Marketplace', path: '/commerce/marketplace', feature: 'marketplace' },
      { label: 'Create Store', path: '/commerce/create-store', feature: 'store_creation' },
    ]
  },
  {
    title: 'Services',
    items: [
      { label: 'Matrise Pickup - P2P Delivery', path: '/pickup', feature: 'pickup_home' },
      { label: 'Become a Driver', path: '/pickup/driver-registration', feature: 'pickup_driver_registration' },
    ]
  },
  {
    title: 'Predictions',
    items: [
      { label: 'Pool Predictions', path: '/app/predictions', feature: 'predictions' },
    ]
  },
  {
    title: 'Community',
    items: [
      { label: 'Groups', path: '/user/groups', feature: 'groups' },
      { label: 'Matrise Connect', path: '/connect', feature: 'connect_feed' },
    ]
  },
  {
    title: 'Earn',
    items: [
      { label: 'Referrals', path: '/user/referrals', feature: 'referrals' },
      { label: 'Tasks & Rewards', path: '/user/tasking', feature: 'tasking' },
    ]
  },
];

/**
 * Search category to feature mapping
 */
export const SEARCH_CATEGORY_FEATURES = {
  'groups': 'groups',
  'products': 'marketplace',
  'predictions': 'predictions',
  'agents': 'agent_network',
};
